import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import DashboardView from "./views/DashboardView.vue";
import ProductListView from "./views/ProductListView.vue";
import ProductDetailView from "./views/ProductDetailView.vue";
import ExpenseRequestView from "./views/ExpenseRequestView.vue";
import ApprovalDetailView from "./views/ApprovalDetailView.vue";
import IntegrationView from "./views/IntegrationView.vue";
import LoginView from "./views/LoginView.vue";
import ChangePasswordView from "./views/ChangePasswordView.vue";
import ModulePlaceholderView from "./views/ModulePlaceholderView.vue";
import CustomerServiceWorkbenchView from "./views/CustomerServiceWorkbenchView.vue";
import StoreWorkbenchView from "./views/StoreWorkbenchView.vue";
import NavigationSettingsView from "./views/NavigationSettingsView.vue";

const placeholder = (path: string, title: string, section: string, description: string, phase = "待接入"): RouteRecordRaw => ({
  path,
  component: ModulePlaceholderView,
  meta: { title, section, description, phase }
});

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: "/login", component: LoginView, meta: { title: "登录", public: true } },
    { path: "/change-password", component: ChangePasswordView, meta: { title: "修改初始密码", public: true } },
    { path: "/", component: DashboardView, meta: { title: "我的工作台", section: "工作台" } },
    placeholder("/tasks", "我的待办", "工作台", "集中查看审批、阶段放行、费用补充和成本确认等待办。"),
    placeholder("/alerts", "异常提醒", "工作台", "汇总同步失败、数据冲突、超预算和待确认成本。"),

    { path: "/products", component: ProductListView, meta: { title: "产品台账", section: "产品中心" } },
    placeholder("/product-initiatives", "新品立项", "产品中心", "从工作台创建产品主档和全局产品编号。", "下一批开发"),
    placeholder("/lifecycle", "生命周期看板", "产品中心", "按照立项、设计打样、试产、量产、上市和复盘管理阶段任务。", "下一批开发"),
    placeholder("/sku-batches", "SKU 与生产批次", "产品中心", "维护商城 SKU、ERPNext Item 与生产批次映射。", "等待 ERPNext 联调"),
    { path: "/products/:id", component: ProductDetailView, meta: { title: "产品详情", section: "产品中心" } },

    placeholder("/marketing/plans", "宣发计划", "营销中心", "按产品和宣发阶段管理营销计划。", "第三阶段"),
    placeholder("/marketing/calendar", "任务日历", "营销中心", "按负责人和时间查看营销任务。", "第三阶段"),
    placeholder("/marketing/assets", "素材与参考链接", "营销中心", "沉淀素材、竞品链接、AI 要点与人工确认记录。", "第三阶段"),
    placeholder("/marketing/templates", "模板管理", "营销中心", "维护可调整的多阶段宣发模板。", "第三阶段"),

    placeholder("/approvals", "待我审批", "审批与费用", "集中处理按组织、角色、金额和预算状态路由的审批任务。", "审批配置后启用"),
    placeholder("/applications", "我的申请", "审批与费用", "查看支出申请、员工报销和供应商付款的处理进度。", "审批配置后启用"),
    placeholder("/expenses", "费用台账", "审批与费用", "按产品、部门、类别和状态追溯费用与归集明细。", "审批配置后启用"),
    placeholder("/budgets", "预算", "审批与费用", "查看预算占用、超预算提醒与升级审批。", "预算规则待配置"),
    { path: "/expenses/new", component: ExpenseRequestView, meta: { title: "支出申请", section: "审批与费用" } },
    { path: "/approvals/:id", component: ApprovalDetailView, meta: { title: "审批详情", section: "审批与费用" } },

    placeholder("/analysis/profit", "产品毛利", "经营分析", "按产品、SKU 和批次下钻净收入、成本与贡献利润。", "第二阶段"),
    placeholder("/analysis/sales", "销售与退款", "经营分析", "核对商城订单、取消、退款和重复事件。", "等待商城数据契约"),
    placeholder("/analysis/costs", "成本待确认", "经营分析", "区分批次实际成本与暂估成本，并保留版本和重算记录。", "等待 ERPNext 成本接口"),

    { path: "/integrations", component: IntegrationView, meta: { title: "应用中心", section: "业务应用" } },
    { path: "/apps/store", component: StoreWorkbenchView, meta: { title: "小程序商城", section: "业务应用" } },
    { path: "/apps/customer-service", redirect: "/apps/customer-service/online/current" },
    { path: "/apps/customer-service/online", redirect: "/apps/customer-service/online/current" },
    { path: "/apps/customer-service/online/:view", component: CustomerServiceWorkbenchView, meta: { title: "在线客服", section: "客服工作台" } },
    { path: "/apps/customer-service/manage", redirect: "/apps/customer-service/manage/workgroups" },
    { path: "/apps/customer-service/manage/:view", component: CustomerServiceWorkbenchView, meta: { title: "客服管理", section: "客服工作台" } },
    { path: "/apps/customer-service/tickets/:pathMatch(.*)*", redirect: "/apps/customer-service/online/current" },

    placeholder("/system/staff", "组织与职员", "系统管理", "管理职员、组织、岗位以及企微、手机号和密码绑定。", "权限模型已建立"),
    placeholder("/system/roles", "角色权限", "系统管理", "配置菜单、操作、数据范围与子系统访问级别。", "权限模型已建立"),
    placeholder("/system/workflows", "审批流程", "系统管理", "按事项、金额、部门、产品和超预算状态配置审批。", "流程配置待开发"),
    placeholder("/system/integration-logs", "集成与日志", "系统管理", "查看同步失败、重试、对账和审计记录。", "Outbox 基线已建立"),
    { path: "/system/navigation", component: NavigationSettingsView, meta: { title: "导航菜单配置", section: "系统管理" } },
    placeholder("/knowledge", "知识手册", "帮助", "工作台使用手册正在整理中。"),
    placeholder("/help", "帮助中心", "帮助", "常见问题与支持入口正在整理中。"),
    { path: "/:pathMatch(.*)*", redirect: "/" }
  ]
});
